import styled from 'styled-components'
import { arrayMove } from '@dnd-kit/sortable'
import { useMedia } from 'use-media'
import { useState } from 'react'

import {
  Box,
  Button,
  ContextualActionBar,
  Dialog,
  Icon,
  MoreOptions,
  Text,
  TransparentButton,
  mediaQueries,
} from '@redriverpress/system'

import {
  ManageFlashcardsDialog,
  PrintFlashcardsDialog,
  SaveFlashcardsToSetDialog,
} from 'Packages/flashcards'

import SelectionDrawer from './SelectionDrawer'
import useSelection from '../../hooks/useSelection'

const ClearSelectionButton = styled(TransparentButton).attrs({
  borderRadius: 'rounded',
  color: 'red900',
  fontSize: 0,
})`
  &:focus {
    box-shadow: 0 0 0 2px ${props => props.theme.colors.red900};
  }
`

export default function SelectionManagerDefault() {
  const isMd = useMedia(mediaQueries.md)

  const {
    selection,
    setSelection,
    editSelectionUserTitle,
    removeFromSelection,
    clearSelection,
  } = useSelection()

  const [saveSelectionDialogOpen, setSaveSelectionDialogOpen] = useState(false)
  const [manageDialogOpen, setManageDialogOpen] = useState(false)
  const [printDialogOpen, setPrintDialogOpen] = useState(false)
  const [clearSelectionDialogOpen, setClearSelectionDialogOpen] = useState(
    false
  )

  const handleClearSelection = () => {
    clearSelection()
    setClearSelectionDialogOpen(false)
    setManageDialogOpen(false)
  }

  const ClearSelectionNode = (
    <Dialog.Root
      open={clearSelectionDialogOpen}
      onOpenChange={open => setClearSelectionDialogOpen(open)}
    >
      <Box position='relative'>
        <Dialog.Trigger asChild>
          <ClearSelectionButton>
            <Icon icon='trash-alt' pr={2} />
            <Text as='span' fontWeight={600}>
              Clear Selection
            </Text>
          </ClearSelectionButton>
        </Dialog.Trigger>
      </Box>
      <Dialog.Portal>
        <Dialog.Overlay zIndex={7} />
        <Dialog.Content asChild>
          <Box
            position='fixed'
            top={0}
            bottom={0}
            left={0}
            right={0}
            zIndex={7}
            display='flex'
            alignItems='center'
            justifyContent='center'
            style={{ pointerEvents: 'none' }}
          >
            <Box
              bg='white'
              p={3}
              borderRadius='rounded'
              m={2}
              style={{ pointerEvents: 'auto' }}
            >
              <Text textAlign='center'>
                Are you sure you want to clear your&nbsp;selection?
              </Text>
              <Box display='flex' justifyContent='center' mt={3}>
                <Button small secondary mr={2} onClick={handleClearSelection}>
                  <Icon icon='check' pr={2} />
                  Yes, Clear
                </Button>
                <Button
                  small
                  onClick={() => setClearSelectionDialogOpen(false)}
                >
                  <Icon icon='times' pr={2} />
                  No, Keep
                </Button>
              </Box>
            </Box>
          </Box>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )

  return (
    <>
      {isMd ? (
        <SelectionDrawer
          onSaveAction={() => setSaveSelectionDialogOpen(true)}
          onPrintAction={() => setPrintDialogOpen(true)}
          deleteAllAction={ClearSelectionNode}
        />
      ) : (
        <>
          <ContextualActionBar
            message={
              <Text>
                <Text as='span' fontWeight={600}>
                  {`${selection.length} ${
                    selection.length === 1 ? 'flashcard' : 'flashcards'
                  }`}
                </Text>{' '}
                selected
              </Text>
            }
            isOpen={Boolean(selection.length) && !manageDialogOpen}
            action={
              <MoreOptions>
                <MoreOptions.Button
                  icon='images'
                  label='Save to Set…'
                  onClick={() => setSaveSelectionDialogOpen(true)}
                />
                <MoreOptions.Button
                  icon='print'
                  label='Print…'
                  onClick={() => setPrintDialogOpen(true)}
                />
                <MoreOptions.Button
                  icon='edit'
                  label='Manage…'
                  onClick={() => setManageDialogOpen(true)}
                />
              </MoreOptions>
            }
          />

          <ManageFlashcardsDialog
            open={manageDialogOpen}
            onOpenChange={open => setManageDialogOpen(open)}
            onClose={() => setManageDialogOpen(false)}
            deleteAllAction={ClearSelectionNode}
            flashcards={selection}
            onRemove={id => removeFromSelection([id])}
            onRename={editSelectionUserTitle}
            onReorder={({ flashcards, oldIndex, newIndex }) =>
              setSelection(arrayMove(flashcards, oldIndex, newIndex))
            }
            title='Unsaved selection'
            primaryActionLabel={
              <>
                <Icon icon='images' pr={2} />
                Save to Set
              </>
            }
            onPrimaryAction={() => setSaveSelectionDialogOpen(true)}
            onPrint={() => {}}
          />
        </>
      )}

      <SaveFlashcardsToSetDialog
        flashcards={selection}
        onClose={() => setSaveSelectionDialogOpen(false)}
        onSaved={clearSelection}
        onOpenChange={open => setSaveSelectionDialogOpen(open)}
        open={saveSelectionDialogOpen}
      />

      <PrintFlashcardsDialog
        flashcards={selection}
        open={printDialogOpen}
        onOpenChange={open => setPrintDialogOpen(open)}
      />
    </>
  )
}
